import React from 'react';
import { cn } from '../../lib/utils';

interface Company {
  name: string;
  x: number; // 0-100 scale
  y: number; // 0-100 scale
  color?: string;
}

interface PositioningMatrixProps {
  companies: Company[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  className?: string;
}

export const PositioningMatrix: React.FC<PositioningMatrixProps> = ({
  companies,
  xAxisLabel = "Specialization Level",
  yAxisLabel = "Approach",
  className
}) => {
  // Return null if no companies data is provided
  if (!companies || companies.length === 0) {
    return null;
  }

  const companiesToRender = companies;

  return (
    <div className={cn("w-full my-8", className)}>
      <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 mb-6 text-left">Competitive Positioning Matrix</h3>
        
        <div className="relative" style={{ paddingBottom: '75%' }}>
          <div className="absolute inset-0">
            {/* Grid background */}
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              {/* Grid lines */}
              <defs>
                <pattern id="grid" width="25" height="25" patternUnits="userSpaceOnUse">
                  <path d="M 25 0 L 0 0 0 25" fill="none" stroke="#E5E7EB" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#grid)" />
              
              {/* Main axes */}
              <line x1="50" y1="0" x2="50" y2="100" stroke="#9CA3AF" strokeWidth="1" />
              <line x1="0" y1="50" x2="100" y2="50" stroke="#9CA3AF" strokeWidth="1" />
              
              {/* Quadrant backgrounds */}
              <rect x="0" y="0" width="50" height="50" fill="#FEF3C7" opacity="0.1" />
              <rect x="50" y="0" width="50" height="50" fill="#DBEAFE" opacity="0.1" />
              <rect x="0" y="50" width="50" height="50" fill="#FED7AA" opacity="0.1" />
              <rect x="50" y="50" width="50" height="50" fill="#D1FAE5" opacity="0.1" />
            </svg>
            
            {/* Company positions */}
            {companiesToRender.map((company, index) => (
              <div
                key={index}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all hover:scale-110"
                style={{
                  left: `${company.x}%`,
                  bottom: `${company.y}%`,
                }}
              >
                <div 
                  className="relative group cursor-pointer"
                  style={{ backgroundColor: company.color || '#3B82F6' }}
                >
                  <div className="w-3 h-3 rounded-full ring-4 ring-white shadow-lg" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-gray-900 text-white text-xs rounded-lg py-1 px-3 whitespace-nowrap">
                      {company.name}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1">
                        <div className="border-4 border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1">
                  <span className="text-xs font-medium text-gray-700 whitespace-nowrap bg-white px-1 rounded shadow-sm">
                    {company.name}
                  </span>
                </div>
              </div>
            ))}
            
            {/* Axis labels */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full mt-4">
              <span className="text-sm font-medium text-gray-700">{xAxisLabel}</span>
            </div>
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-full -rotate-90 origin-center">
              <span className="text-sm font-medium text-gray-700">{yAxisLabel}</span>
            </div>
            
            {/* Quadrant labels */}
            <div className="absolute top-2 left-2">
              <span className="text-xs text-gray-600 bg-white/80 px-2 py-1 rounded">Low / Slide-First</span>
            </div>
            <div className="absolute top-2 right-2">
              <span className="text-xs text-gray-600 bg-white/80 px-2 py-1 rounded">High / Slide-First</span>
            </div>
            <div className="absolute bottom-2 left-2">
              <span className="text-xs text-gray-600 bg-white/80 px-2 py-1 rounded">Low / Speech-First</span>
            </div>
            <div className="absolute bottom-2 right-2">
              <span className="text-xs text-gray-600 bg-white/80 px-2 py-1 rounded">High / Speech-First</span>
            </div>
          </div>
        </div>
        
        {/* Legend */}
        <div className="mt-6 flex flex-wrap justify-center gap-4">
          {companiesToRender.map((company, index) => (
            <div key={index} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: company.color || '#3B82F6' }}
              />
              <span className="text-sm text-gray-700">{company.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};